import {
  BarChart3, Building2, FolderTree, Gift, GalleryHorizontal, Globe2, LayoutDashboard, ListTree, Megaphone, Package, Palette, Paintbrush, Percent,
  FileText, Receipt, Settings, Share2, Store, Ticket, Users, Wallet, Warehouse, type LucideIcon,
} from 'lucide-react';
import type { FeatureKey } from '@/types';
import type { Permission } from '@/lib/permissions';

export interface AdminNavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Hidden (and the route blocked) unless the actor has this permission. */
  permission: Permission;
  /** Hidden (and the route blocked) unless the tenant has this module enabled. */
  feature?: FeatureKey;
  end?: boolean;
}
export interface AdminNavSection { title: string; items: AdminNavItem[] }

/** Single source of truth for sidebar entries AND route guards (see app/router.tsx). */
export const ADMIN_NAV: AdminNavSection[] = [
  {
    title: 'Overview', items: [
      { label: 'World view', to: '/admin/world', icon: Globe2, permission: 'analytics.view' },
      { label: 'Dashboard', to: '/admin', icon: LayoutDashboard, permission: 'dashboard.view', end: true },
    ],
  },
  {
    title: 'Catalog', items: [
      { label: 'Products', to: '/admin/products', icon: Package, permission: 'catalog.read', feature: 'products' },
      { label: 'Categories', to: '/admin/categories', icon: FolderTree, permission: 'catalog.read', feature: 'categories' },
      { label: 'Inventory', to: '/admin/inventory', icon: Warehouse, permission: 'catalog.read', feature: 'products' },
    ],
  },
  {
    title: 'Sales', items: [
      { label: 'Orders', to: '/admin/orders', icon: Receipt, permission: 'orders.read', feature: 'orders' },
      { label: 'B2B quotes', to: '/admin/b2b-quotes', icon: FileText, permission: 'orders.read', feature: 'orders' },
      { label: 'Customers', to: '/admin/customers', icon: Users, permission: 'customers.read' },
      { label: 'Sellers', to: '/admin/sellers', icon: Store, permission: 'settings.manage' },
      { label: 'Operations', to: '/admin/operations', icon: Building2, permission: 'settings.manage' },
    ],
  },
  {
    title: 'Marketing', items: [
      { label: 'Coupons', to: '/admin/coupons', icon: Ticket, permission: 'marketing.manage', feature: 'coupons' },
      { label: 'Banners', to: '/admin/banners', icon: GalleryHorizontal, permission: 'marketing.manage', feature: 'banners' },
      { label: 'Offers', to: '/admin/offers', icon: Percent, permission: 'marketing.manage', feature: 'coupons' },
      { label: 'Campaigns', to: '/admin/campaigns', icon: Megaphone, permission: 'marketing.manage', feature: 'campaigns' },
    ],
  },
  {
    title: 'Retention', items: [
      { label: 'Wallet', to: '/admin/wallet', icon: Wallet, permission: 'wallet.read', feature: 'wallet' },
      { label: 'Loyalty', to: '/admin/loyalty', icon: Gift, permission: 'loyalty.manage', feature: 'loyalty' },
      { label: 'Referrals', to: '/admin/referrals', icon: Share2, permission: 'referrals.manage', feature: 'affiliate' },
    ],
  },
  {
    title: 'Store', items: [
      { label: 'Store settings', to: '/admin/store', icon: Store, permission: 'store.manage' },
      { label: 'Branding', to: '/admin/branding', icon: Palette, permission: 'store.manage' },
      { label: 'Navigation', to: '/admin/navigation', icon: ListTree, permission: 'store.manage' },
      { label: 'Theme', to: '/admin/theme', icon: Paintbrush, permission: 'store.manage' },
    ],
  },
  { title: 'Analytics', items: [{ label: 'Reports', to: '/admin/analytics', icon: BarChart3, permission: 'analytics.view', feature: 'analytics' }] },
  { title: 'System', items: [{ label: 'Settings', to: '/admin/settings', icon: Settings, permission: 'settings.manage' }] },
];

export const findNavItem = (pathname: string) =>
  ADMIN_NAV.flatMap((s) => s.items).filter((i) => (i.end ? pathname === i.to : pathname === i.to || pathname.startsWith(i.to + '/'))).sort((a, b) => b.to.length - a.to.length)[0];
